/**
 * Global error handlers: surface issues as toast popups instead of full-page overlays.
 * Network/offline cases stay quiet (No Internet page / existing flow).
 */

import { notifyError } from './utils/appToast'

let globalErrorHandlersInstalled = false

function isNetworkLikeMessage(msg: string): boolean {
  const m = msg.toLowerCase()
  return (
    m.includes('network') ||
    m.includes('fetch') ||
    m.includes('failed to fetch') ||
    m.includes('networkerror') ||
    m.includes('connection') ||
    m.includes('offline') ||
    m.includes('econnrefused') ||
    m.includes('timeout') ||
    m.includes('load failed')
  )
}

export const setupGlobalErrorHandlers = () => {
  if (globalErrorHandlersInstalled) {
    return
  }
  globalErrorHandlersInstalled = true

  // Hide CRA/webpack full-screen overlays only (dev); do not hide arbitrary "error" UI.
  try {
    const id = 'global-error-handler-overlay-hide'
    if (!document.getElementById(id)) {
      const style = document.createElement('style')
      style.id = id
      style.innerHTML = `
        iframe[title="React Error Overlay"],
        #webpack-dev-server-client-overlay {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `
      document.head.appendChild(style)
    }
  } catch {
    // ignore
  }

  window.addEventListener(
    'error',
    (event) => {
      const t = (event as ErrorEvent).target
      if (t && t !== window && t !== document.documentElement) {
        return
      }
      const errorMessage = event.error?.message || event.message || ''
      if (isNetworkLikeMessage(errorMessage)) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      const text =
        errorMessage ||
        (typeof event.message === 'string' ? event.message : 'Something went wrong')
      notifyError(text.length > 180 ? `${text.slice(0, 177)}…` : text)
      event.preventDefault()
      event.stopPropagation()
    },
    true,
  )

  window.addEventListener(
    'unhandledrejection',
    (event) => {
      const reasonMessage = event.reason?.message || String(event.reason ?? '')
      if (isNetworkLikeMessage(reasonMessage)) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      const text =
        reasonMessage && reasonMessage !== '[object Object]'
          ? reasonMessage
          : 'Request failed. Please try again.'
      notifyError(text.length > 180 ? `${text.slice(0, 177)}…` : text)
      event.preventDefault()
      event.stopPropagation()
    },
    true,
  )
}

export const safeAsync = <T extends (...args: any[]) => Promise<any>>(fn: T): T => {
  return ((...args: any[]) => {
    return Promise.resolve(fn(...args)).catch((error) => {
      const msg = error?.message || String(error)
      if (!isNetworkLikeMessage(msg)) notifyError(msg)
      return Promise.resolve()
    })
  }) as T
}

export const safeSync = <T extends (...args: any[]) => any>(fn: T): T => {
  return ((...args: any[]) => {
    try {
      return fn(...args)
    } catch (error: any) {
      const msg = error?.message || String(error)
      if (!isNetworkLikeMessage(msg)) notifyError(msg)
      return undefined
    }
  }) as T
}
